import type { PriceBookColumnKey } from '../types/BlockContent/PriceBookContent';

/**
 * THE RATE SCHEDULE IS THREE TABLES, NOT ONE (design ruling 2026-09-02).
 *
 * The document renders three sections, each under its own subheading and each with its own first
 * column and column subset:
 *   Equipment rates       · first column "Service"      · the non-meta columns below
 *   Crew rates            · first column "Role"         · the crew-only columns below
 *   Rate sheet line items · first column "Description"  · fixed Unit + Rate
 * A section renders only when its own selection is non-empty; with all three empty the element
 * shows one empty state.
 *
 * THE DATA FOR TWO OF THEM DOES NOT EXIST YET, and that is deliberate rather than forgotten: the
 * three sections come from three independent selections (equipment / crew / items), while
 * `PriceBookContent` today carries ONE `serviceItemIds` list. The authoring view draws all three
 * sections regardless (the unwired two render mocked as well, so an author can see the shape),
 * while the printed page can only draw rows the content actually supplies. When the contract lands
 * (Part 15), each section gets its own selection and its own rows; nothing about this module
 * changes.
 *
 * AND TWO COLUMNS ARE NOT COLUMNS: `job_type` and `flat_price` render as a META LINE under the
 * service name (joined by " · "), never as their own column — that is why they live in `meta`
 * rather than `columns`, and why `priceBookSectionMeta` is NOT filtered by the author's visible
 * set (a meta key is part of what the row IS, not something the author switches on).
 */
export type PriceBookSectionKey = 'equipment' | 'crew' | 'items';

export interface PriceBookSection {
  key: PriceBookSectionKey;
  /** The subheading above the section's own table. */
  label: string;
  /** The first column's header — the thing each row names. */
  rowHeader: string;
  /** Which toggleable columns this section can show, in display order. */
  columns: readonly PriceBookColumnKey[];
  /** Columns that render as the meta line under the row's name instead of as columns. */
  meta: readonly PriceBookColumnKey[];
  /** Fixed columns this section always shows, regardless of the author's toggles. */
  fixed?: readonly PriceBookColumnKey[];
}

export const PRICE_BOOK_SECTIONS: readonly PriceBookSection[] = [
  {
    key: 'equipment',
    label: 'Equipment rates',
    rowHeader: 'Service',
    // Equipment is seven columns; `min_charge` is not one of them — it exists on the pricing data
    // but is not a column this document prints.
    columns: ['unit', 'rate', 'ot_rate', 'ot_rate_2', 'volume_rate', 'mobilization', 'travel'],
    meta: ['job_type', 'flat_price']
  },
  {
    key: 'crew',
    label: 'Crew rates',
    rowHeader: 'Role',
    // A crew role has no unit, yardage or mobilization.
    columns: ['rate', 'ot_rate', 'ot_rate_2', 'travel'],
    meta: []
  },
  {
    key: 'items',
    label: 'Rate sheet line items',
    rowHeader: 'Description',
    // Fixed, not toggleable: this table's columns are hardcoded.
    columns: [],
    meta: [],
    fixed: ['unit', 'rate']
  }
];

/** The columns this section will actually draw, given the author's visible set. */
export function priceBookSectionColumns(section: PriceBookSection, visibleKeys: readonly string[]): PriceBookColumnKey[] {
  if (section.fixed) return [...section.fixed];
  return section.columns.filter((key) => visibleKeys.includes(key));
}

/**
 * The meta keys this section draws under each row name. Unconditional — see the module docstring
 * for why these are never filtered against the author's visible set.
 */
export function priceBookSectionMeta(section: PriceBookSection): PriceBookColumnKey[] {
  return [...section.meta];
}
